import { useState, useEffect, useCallback } from "react";
import { supabase, openedFromRecoveryLink } from "./supabase";
import { goFetch, goDownload } from "./api";
import type { AppliedDiscount } from "./discount";
import type { SlotAvailability, Booking, Customer, SlotBookingInfo, CreditTransaction, AdminVoucher, AdminDiscountCode, DiscountCodeLimits, WorkshopKind, WorkshopKindID, RedeemVoucherResult } from "./types";
import type { User, Session } from "@supabase/supabase-js";

/** Format a Date as YYYY-MM-DD in local timezone (not UTC) */
export function toLocalDateStr(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  // The event below can fire before the embed mounts; the flag from the URL cannot be missed.
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(openedFromRecoveryLink);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: string, session: Session | null) => {
      setUser(session?.user ?? null);
      if (event === "PASSWORD_RECOVERY") {
        setIsPasswordRecovery(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signUp = async (email: string, password: string, name: string) => {
    // Odkaz v potvrdzovacom emaile ma viest na stranku, kde widget bezi, nie na
    // Site URL zo Supabase. Domena musi byt v Supabase Auth -> Redirect URLs.
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name }, emailRedirectTo: currentReturnURL() },
    });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: currentReturnURL() });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    return { error };
  };

  const clearPasswordRecovery = () => setIsPasswordRecovery(false);

  return { user, loading, isPasswordRecovery, signIn, signUp, signOut, resetPassword, updatePassword, clearPasswordRecovery };
}

/**
 * Admin check for the given email. `loading` is true until the answer for the
 * *current* email is known — it flips back to true synchronously whenever the
 * email changes, so consumers never see a stale "not admin" for a new user.
 */
export function useIsAdmin(userEmail: string | undefined) {
  const [checked, setChecked] = useState<{ email: string | undefined; isAdmin: boolean } | null>(null);

  useEffect(() => {
    if (!userEmail) {
      setChecked({ email: userEmail, isAdmin: false });
      return;
    }
    goFetch<{ is_admin: boolean }>("/api/me/admin")
      .then(({ is_admin }) => setChecked({ email: userEmail, isAdmin: is_admin }))
      .catch(() => setChecked({ email: userEmail, isAdmin: false }));
  }, [userEmail]);

  const loading = checked === null || checked.email !== userEmail;
  return { isAdmin: !loading && checked.isAdmin, loading };
}

/** Query params covering the 7-day week starting at weekStart */
function weekRangeParams(weekStart: Date): Record<string, string> {
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);
  return { from: toLocalDateStr(weekStart), to: toLocalDateStr(weekEnd) };
}

/**
 * Shared slot loader. When `enabled` is false no request is made and `loading`
 * stays true (nothing has been loaded yet); the fetch starts once `enabled`
 * flips to true.
 */
function useSlotsFromEndpoint(path: string, auth: boolean, weekStart: Date, enabled: boolean) {
  const [slots, setSlots] = useState<SlotAvailability[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchSlots = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const data = await goFetch<SlotAvailability[]>(path, {
        auth,
        params: weekRangeParams(weekStart),
      });
      setSlots(data);
    } catch (e) {
      console.error(`Slots fetch error (${path}):`, e);
    }
    setLoading(false);
  }, [path, auth, weekStart, enabled]);

  useEffect(() => {
    fetchSlots();
  }, [fetchSlots]);

  return { slots, loading, refetch: fetchSlots };
}

/** Public view: active slots only (slot_availability). */
export function useSlots(weekStart: Date, enabled = true) {
  return useSlotsFromEndpoint("/api/slots", false, weekStart, enabled);
}

/**
 * Admin view: ALL slots including blocked (admin_slot_availability).
 * Pass `enabled=false` for non-admins (or while the role is still unknown) —
 * the admin endpoint must never be called for regular customers.
 */
export function useAdminSlots(weekStart: Date, enabled = true) {
  return useSlotsFromEndpoint("/api/admin/slots", true, weekStart, enabled);
}

export function useBookings(userId: string | undefined) {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBookings = useCallback(async () => {
    if (!userId) {
      setBookings([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await goFetch<Booking[]>("/api/bookings");
      setBookings(data);
    } catch (e) {
      console.error("Bookings fetch error:", e);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  return { bookings, loading, refetch: fetchBookings };
}

export function useCustomer(userId: string | undefined) {
  const [customer, setCustomer] = useState<Customer | null>(null);

  const fetchCustomer = useCallback(async () => {
    if (!userId) {
      setCustomer(null);
      return;
    }
    try {
      const data = await goFetch<Customer>("/api/me/customer");
      setCustomer(data);
    } catch {
      setCustomer(null);
    }
  }, [userId]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  return { customer, refetchCustomer: fetchCustomer };
}

/**
 * Stranka, na ktoru sa ma zakaznik vratit po Stripe platbe alebo z emailu
 * (potvrdenie registracie, reset hesla).
 *
 * Widget moze bezat vlozeny v cudzej stranke, takze posielame vlastnu URL —
 * inak by Stripe aj Supabase vratili zakaznika na svoju zalozku (app.claystudio.sk,
 * resp. Site URL) namiesto tam, odkial vysiel. Backend overuje URL proti
 * allowliste originov, Supabase proti Redirect URLs; neznamu obe zahodia.
 * Query a hash necham na prijemcovi, aby si mohol pripojit session_id / token.
 */
function currentReturnURL(): string {
  return window.location.origin + window.location.pathname;
}

/** sessionStorage key marking that this tab left for Stripe Checkout and hasn't come back through success/cancel URL yet. */
export const CHECKOUT_PENDING_KEY = "claystudio.checkoutPending";

/**
 * Presmeruje zakaznika na Stripe Checkout.
 *
 * Priradenie location.href bezi az po odpovedi API, teda mimo kliknutia. Chrome taku
 * navigaciu bez gesta tresta: zaznam tejto stranky v historii oznaci ako preskocitelny
 * (history manipulation intervention), pripadne ho pri este nenacitanej stranke rovno
 * nahradi. Tlacidlo Spat na Stripe potom skoci na stranku pred claystudio.sk.
 * Preto pred odchodom pridame do historie este jeden zaznam tej istej URL: ak ho
 * prehliadac obetuje, Spat stale skonci tu. Marker v sessionStorage dovoli App
 * rozpoznat navrat cez Spat (bez session_id / payment parametrov).
 */
export function redirectToCheckout(checkoutUrl: string) {
  try {
    sessionStorage.setItem(CHECKOUT_PENDING_KEY, "1");
  } catch {
    // sessionStorage can be unavailable (privacy mode); the redirect still works.
  }
  window.history.pushState(window.history.state, "", window.location.href);
  window.location.href = checkoutUrl;
}

export async function createMultiBooking(slotIds: number[], numPersons: number = 1, discountCode?: string) {
  try {
    const data = await goFetch<{
      booking_count: number;
      booking_group_id: string;
      customer_code: string;
      total_price_cents: number;
      discount_code?: string;
      discount_percent?: number;
      discount_amount_cents?: number;
      discount_cents?: number;
      credit_used_cents: number;
      amount_due_cents: number;
      num_persons: number;
      status: string;
      checkout_url?: string;
    }>("/api/bookings", {
      method: "POST",
      body: {
        slot_ids: slotIds,
        num_persons: numPersons,
        return_url: currentReturnURL(),
        ...(discountCode ? { discount_code: discountCode } : {}),
      },
    });
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

/** Check a discount code before booking. Error messages come from the API in Slovak. */
export async function checkDiscountCode(code: string) {
  try {
    const data = await goFetch<AppliedDiscount>("/api/discount-codes/check", {
      method: "POST",
      body: { code },
    });
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

// ── Credit functions ──

export function useCreditBalance(userId: string | undefined) {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState<CreditTransaction[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchBalance = useCallback(async () => {
    if (!userId) {
      setBalance(0);
      setTransactions([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const resp = await goFetch<{ balance_cents: number; transactions: CreditTransaction[] }>("/api/me/credit");
      setBalance(resp.balance_cents);
      setTransactions(resp.transactions ?? []);
    } catch (e) {
      console.error("Credit balance fetch error:", e);
    }
    setLoading(false);
  }, [userId]);

  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  return { balance, transactions, loading, refetchBalance: fetchBalance };
}

/** Turn a gift voucher code into credit. Error messages come from the API in Slovak. */
/** Phase 1: code only (credit → done; workshop → list of options). Phase 2: code + slot_id books the seat. */
export async function redeemVoucher(code: string, slotId?: number) {
  try {
    const data = await goFetch<RedeemVoucherResult>(
      "/api/me/vouchers/redeem",
      { method: "POST", body: slotId ? { code, slot_id: slotId } : { code } }
    );
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

/** Apply current credit to a pending booking; confirms it or returns a fresh Stripe URL for the remainder. */
export async function payBooking(bookingGroupId: string) {
  try {
    const data = await goFetch<{
      status: "confirmed" | "pending_payment";
      credit_used_cents: number;
      amount_due_cents: number;
      checkout_url?: string;
    }>(`/api/bookings/${bookingGroupId}/pay`, {
      method: "POST",
      body: { return_url: currentReturnURL() },
    });
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function userCancelBooking(bookingGroupId: string) {
  try {
    const data = await goFetch<{
      cancelled_count: number;
      refund_credit_cents: number;
      balance_cents: number;
    }>(`/api/bookings/${bookingGroupId}`, {
      method: "DELETE",
    });
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminListVouchers(status?: string) {
  try {
    const data = await goFetch<AdminVoucher[]>("/api/admin/vouchers", status ? { params: { status } } : undefined);
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminCreateVoucher(req: {
  code: string;
  type: "credit" | "workshop";
  workshop_kind?: WorkshopKindID;
  value_cents: number;
  expires_at: string;
  note: string;
}) {
  try {
    const data = await goFetch<AdminVoucher>("/api/admin/vouchers", { method: "POST", body: req });
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminVoidVoucher(id: number) {
  try {
    const data = await goFetch<AdminVoucher>(`/api/admin/vouchers/${id}/void`, { method: "POST" });
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminListDiscountCodes() {
  try {
    const data = await goFetch<AdminDiscountCode[]>("/api/admin/discount-codes");
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminCreateDiscountCode(req: { code: string; percent?: number; amount_cents?: number; expires_at: string; max_uses: number | null; note: string }) {
  try {
    const data = await goFetch<AdminDiscountCode>("/api/admin/discount-codes", { method: "POST", body: req });
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminUpdateDiscountCode(id: number, req: DiscountCodeLimits) {
  try {
    const data = await goFetch<AdminDiscountCode>(`/api/admin/discount-codes/${id}`, { method: "PUT", body: req });
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminDownloadVoucherPDF(id: number, code: string) {
  try {
    await goDownload(`/api/admin/vouchers/${id}/pdf`, `darcekovy-poukaz-${code}.pdf`);
    return { error: null };
  } catch (e: any) {
    return { error: { message: e.message } };
  }
}

export async function adminAdjustCredit(userId: string, amountCents: number, description: string) {
  try {
    const data = await goFetch<{ transaction_id: number; balance_cents: number }>(
      `/api/admin/customers/${userId}/credits`,
      { method: "POST", body: { amount_cents: amountCents, description } }
    );
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminGetCustomerCredit(userId: string) {
  try {
    const data = await goFetch<{ balance_cents: number; transactions: any[] }>(
      `/api/admin/customers/${userId}/credits`
    );
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminSearchCustomers(query: string) {
  try {
    const data = await goFetch<any[]>("/api/admin/customers", { params: { q: query } });
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminGetRecentCustomers() {
  try {
    const data = await goFetch<any[]>("/api/admin/customers");
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

// ── Admin API functions ──

/** Generate hourly slots for one week. `days` is Monday-first, 7 entries; start/end 0 = closed. */
export async function adminGenerateSlots(
  weekStart: string,
  days?: Array<{ start: number; end: number }>,
  resources?: Array<{
    resourceType: string;
    capacity: number;
    priceCents: number;
  }>
) {
  try {
    const data = await goFetch<{ created: number; skipped: number; blocked_by_workshop: number; week_start: string }>(
      "/api/admin/slots/bulk",
      {
        method: "POST",
        body: {
          week_start: weekStart,
          schedule: days ? { days } : undefined,
          resources: resources?.map((r) => ({
            resource_type: r.resourceType,
            capacity: r.capacity,
            price_cents: r.priceCents,
          })),
        },
      }
    );
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

/** Admin: every slot (incl. blocked) on one day; used to limit workshop times to generated hours. */
export async function adminGetDaySlots(date: string) {
  try {
    const data = await goFetch<SlotAvailability[]>("/api/admin/slots", { params: { from: date, to: date } });
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

/** Public catalogue of workshop kinds (id, label, default price). */
export async function getWorkshopKinds() {
  try {
    const data = await goFetch<WorkshopKind[]>("/api/workshop-kinds");
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminCreateWorkshop(params: {
  workshopKind: WorkshopKindID;
  title: string;
  date: string;
  startTime: string;
  endTime: string;
  capacity: number;
  priceCents: number;
  usesResources: string[];
}) {
  try {
    const data = await goFetch<{
      slot_id: number; workshop_kind: WorkshopKindID; title: string; date: string;
      start_time: string; end_time: string; uses_resources: string[]; blocked_count: number;
    }>("/api/admin/workshops", {
      method: "POST",
      body: {
        workshop_kind: params.workshopKind,
        title: params.title,
        date: params.date,
        start_time: params.startTime,
        end_time: params.endTime,
        capacity: params.capacity,
        price_cents: params.priceCents,
        uses_resources: params.usesResources,
      },
    });
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminToggleSlot(slotId: number, active: boolean) {
  try {
    const data = await goFetch<{ slot_id: number; is_active: boolean }>(
      `/api/admin/slots/${slotId}`,
      { method: "PATCH", body: { active } }
    );
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

/** Mark or clear Clayground for the hour of the slot (both stôl and kruh). */
export async function adminSetClayground(slotId: number, on: boolean) {
  try {
    const data = await goFetch<{ slot_id: number; clayground: boolean; updated: number }>(
      `/api/admin/slots/${slotId}/clayground`,
      { method: "PUT", body: { on } }
    );
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminUpdateSlotCapacity(slotId: number, capacity: number) {
  try {
    const data = await goFetch<{ slot_id: number; capacity: number }>(
      `/api/admin/slots/${slotId}/capacity`,
      { method: "PUT", body: { capacity } }
    );
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminCancelBooking(bookingId: number) {
  try {
    const data = await goFetch<{ booking_id: number; status: string; refund_cents: number }>(
      `/api/admin/bookings/${bookingId}`,
      { method: "DELETE" }
    );
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminCancelBookingGroup(groupId: string) {
  try {
    const data = await goFetch<{ booking_group_id: string; cancelled_count: number; refund_cents: number }>(
      `/api/admin/booking-groups/${groupId}`,
      { method: "DELETE" }
    );
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminDeleteWorkshop(slotId: number) {
  try {
    const data = await goFetch<{
      deleted: boolean; slot_id: number; unblocked_count: number;
      cancelled_bookings: number; refunded_users: number; total_refund_cents: number;
    }>(`/api/admin/workshops/${slotId}`, { method: "DELETE" });
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminGetSlotBookings(slotId: number): Promise<{ data: SlotBookingInfo[] | null; error: Error | null }> {
  try {
    const data = await goFetch<SlotBookingInfo[]>(`/api/admin/slots/${slotId}/bookings`);
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: e };
  }
}

export async function adminRegeneratePIN(groupId: string) {
  try {
    const data = await goFetch<{ booking_group_id: string; pin: string }>(
      `/api/admin/booking-groups/${groupId}/regenerate-pin`,
      { method: "POST" }
    );
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}

export async function adminSetManualPIN(groupId: string, pin: string) {
  try {
    const data = await goFetch<{ booking_group_id: string; pin: string }>(
      `/api/admin/booking-groups/${groupId}/manual-pin`,
      { method: "PUT", body: { pin } }
    );
    return { data, error: null };
  } catch (e: any) {
    return { data: null, error: { message: e.message } };
  }
}
